import { useCallback, useEffect, useState } from 'react'
import { Loader2, Printer, Save } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { useBranch } from '@/contexts/BranchContext'
import { fetchOperationsSettings, updateOperationsSettings } from '@/lib/api/operationsSettings'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import { cn } from '@/lib/utils'

const inputClass = cn(
  'h-9 w-full rounded-lg border border-border/80 bg-background px-2.5 text-sm',
  'focus:outline-none focus:ring-2 focus:ring-ring',
)

export function OperationsSettingsPanel({ className = '' }) {
  const { activeBranchId } = useBranch()
  const [form, setForm] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const settings = await fetchOperationsSettings()
      setForm({
        auto_print_service_slip_on_check_in: Boolean(settings?.auto_print_service_slip_on_check_in),
        stylist_workspace_poll_seconds: settings?.stylist_workspace_poll_seconds ?? 15,
      })
    } catch (err) {
      setForm(null)
      setError(apiErrorMessage(err, 'Unable to load operations settings.'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load, activeBranchId])

  const handleSave = async (event) => {
    event.preventDefault()
    if (!form) return

    setSaving(true)
    setError('')
    setMessage('')

    try {
      const updated = await updateOperationsSettings(form)
      setForm({
        auto_print_service_slip_on_check_in: Boolean(updated?.auto_print_service_slip_on_check_in),
        stylist_workspace_poll_seconds: updated?.stylist_workspace_poll_seconds ?? 15,
      })
      setMessage('Operations settings saved.')
    } catch (err) {
      setError(apiErrorMessage(err, 'Unable to save operations settings.'))
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className={cn('flex items-center gap-2 rounded-xl border border-border/60 bg-card px-4 py-3 text-sm text-muted-foreground', className)}>
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading floor settings…
      </div>
    )
  }

  if (!form) {
    return error ? (
      <div className={cn('rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700', className)}>
        {error}
      </div>
    ) : null
  }

  return (
    <form
      onSubmit={handleSave}
      className={cn('rounded-xl border border-border/60 bg-card px-4 py-3 shadow-luxury-sm', className)}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-foreground">Floor operations</p>
          <p className="text-[11px] text-muted-foreground">
            Check-in printing and stylist tablet refresh interval.
          </p>
        </div>
        <Button type="submit" size="sm" disabled={saving}>
          {saving ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-2 h-3.5 w-3.5" />}
          Save
        </Button>
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <label className="flex items-start gap-2 rounded-lg border border-border/50 bg-muted/20 px-3 py-2 text-sm">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={form.auto_print_service_slip_on_check_in}
            onChange={(e) =>
              setForm((current) => ({
                ...current,
                auto_print_service_slip_on_check_in: e.target.checked,
              }))
            }
          />
          <span>
            <span className="inline-flex items-center gap-1 font-medium">
              <Printer className="h-3.5 w-3.5" aria-hidden />
              Auto-print service slips on check-in
            </span>
            <span className="mt-0.5 block text-[11px] text-muted-foreground">
              Prints stylist slips when the appointment is already linked to a POS ticket.
            </span>
          </span>
        </label>

        <label className="block text-xs">
          Stylist tablet poll (seconds)
          <input
            type="number"
            min={5}
            max={120}
            className={cn(inputClass, 'mt-1')}
            value={form.stylist_workspace_poll_seconds}
            onChange={(e) =>
              setForm((current) => ({
                ...current,
                stylist_workspace_poll_seconds: Number(e.target.value),
              }))
            }
          />
        </label>
      </div>

      {message ? <p className="mt-2 text-xs text-emerald-700">{message}</p> : null}
      {error ? <p className="mt-2 text-xs text-red-700">{error}</p> : null}
    </form>
  )
}
